import { useEffect } from 'react';

import { DEFAULT_NUMBER_OF_LOADS } from '../../constants';
import { useMatchedSiteMaterialItem } from '../../contexts/MatchedSiteMaterialItemContext';
import { useNetTakeRateCalculator } from '../../hooks/useNetTakeRateCalculator/useNetTakeRateCalculator';
import { getInitialPernRevenuePerTonne } from '../../utils/getInitialPernRevenuePerTonne/getInitialPernRevenuePerTonne';
import { getInitialTonnesPerLoad } from '../../utils/getInitialTonnesPerLoad/getInitialTonnesPerLoad';
import { NetTakeRateOverview } from '../NetTakeRateOverview/NetTakeRateOverview';

export const TotalNetTakeRateOverview = () => {
  const {
    totalNetTakeRate,
    initialSellerOffer,
    initialBuyerOffer,
    setTotalNetTakeRate,
    setTotalNetTakeRateLoading,
    siteMaterial,
    targetMargin,
    demandSignal,
    supplySignal,
  } = useMatchedSiteMaterialItem();

  const { calculateNetTakeRate } = useNetTakeRateCalculator({
    onSettled: (ntr) => {
      if (ntr) {
        setTotalNetTakeRate({
          error: undefined,
          isLoading: false,
          rate: Number(ntr.net_take_rate),
        });
        return;
      }
      setTotalNetTakeRate({
        error: undefined,
        isLoading: false,
        rate: undefined,
      });
    },
  });

  useEffect(() => {
    if (totalNetTakeRate) {
      return;
    }
    const sellerPricePerTonne =
      initialSellerOffer?.price_details?.seller_price_per_tonne || supplySignal?.price_per_tonne;
    const sellerCurrency = initialSellerOffer?.price_details?.seller_currency || supplySignal?.currency;
    const buyerPricePerTonne = initialBuyerOffer?.price_details?.buyer_price_per_tonne || demandSignal?.price_per_tonne;
    const buyerCurrency = initialBuyerOffer?.price_details?.buyer_currency || demandSignal?.currency;

    const transportCost =
      initialSellerOffer?.price_details?.transport_cost_per_load ||
      initialBuyerOffer?.price_details?.transport_cost_per_load;
    const transportCurrency =
      initialSellerOffer?.price_details?.transport_cost_currency ||
      initialBuyerOffer?.price_details?.transport_cost_currency;

    if (!sellerPricePerTonne || !sellerCurrency || !buyerPricePerTonne || !buyerCurrency) {
      return;
    }
    if (transportCost === undefined || !transportCurrency) {
      return;
    }

    const pernRevenuePerTonne = getInitialPernRevenuePerTonne(
      initialSellerOffer?.price_details?.pern_revenue_per_tonne
    );
    setTotalNetTakeRateLoading(true);
    calculateNetTakeRate({
      buyerCurrency: buyerCurrency,
      buyerPricePerTonne: buyerPricePerTonne,
      materialId: siteMaterial.material_spec.material.id,
      numberOfLoads: DEFAULT_NUMBER_OF_LOADS,
      pernRevenuePerTonne: pernRevenuePerTonne,
      sellerCurrency: sellerCurrency,
      sellerPricePerTonne: sellerPricePerTonne,
      tonnesPerLoad: getInitialTonnesPerLoad(siteMaterial),
      transportCurrency: transportCurrency,
      transportPricePerTonne: Number(transportCost),
    }).finally(() => {
      setTotalNetTakeRateLoading(false);
    });
  }, [
    calculateNetTakeRate,
    initialBuyerOffer,
    initialSellerOffer,
    setTotalNetTakeRateLoading,
    siteMaterial,
    totalNetTakeRate,
    demandSignal,
    supplySignal,
  ]);

  return <NetTakeRateOverview totalNetTakeRate={totalNetTakeRate} targetMargin={targetMargin} />;
};
